// src/OnlineGame.js
/**
 * OnlineGame component — WebSocket connects to GameRoom Durable Object.
 * Uses PhutballEngine client-side for instant move preview.
 * Full board interaction: placement, multi-waypoint jumps, keyboard nav, mobile layout.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import GameBoard from './GameBoard';
import HistoryPanel from './HistoryPanel';
import LogPanel from './LogPanel';
import Header from './Header';
import PauseMenu from './PauseMenu';
import PhutballEngine from './PhutballEngine';
import CoordinateSystemIcon from './CoordinateSystemIcon';
import { formatBoardCoordinate } from './coordinateFormatting';
import { useAuth } from './AuthContext';
import { TILE_TYPE } from './utils';
import { TIME_CONTROL_LIMITS_MS, TIME_CONTROL_SHORT_LABELS } from './timeControls';
import { trackEvent } from './telemetry';
import './App.css';

// Per-player clock indicators rendered above the board for live timed games.
// The wire format is relative: server sends remainingMs at the moment of
// broadcast and the client counts down using its own monotonic clock from
// the local arrival time. This way clock skew between the worker and the
// player's machine doesn't bias the display.
function GameClocks({ clock, clockReceivedAt, timeControl, players, yourPlayerNum, gameOver }) {
  const [, setTick] = useState(0);
  const limit = clock?.perMoveMs ?? TIME_CONTROL_LIMITS_MS[timeControl];

  useEffect(() => {
    if (!clock || clock.remainingMs == null || gameOver) return;
    let raf = 0;
    const tick = () => {
      setTick(t => t + 1);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [clock, gameOver]);

  if (!clock || limit == null) return null;

  const elapsedSinceReceipt = () =>
    clockReceivedAt != null ? Math.max(0, Date.now() - clockReceivedAt) : 0;

  const renderRemaining = (player) => {
    if (clock.active !== player) return clock.perMoveMs ?? limit;
    if (clock.remainingMs == null) return clock.perMoveMs ?? limit;
    if (gameOver) return Math.max(0, clock.remainingMs - elapsedSinceReceipt());
    return Math.max(0, clock.remainingMs - elapsedSinceReceipt());
  };

  const fmt = (ms) => {
    const total = Math.max(0, ms);
    const seconds = Math.floor(total / 1000);
    const tenths = Math.floor((total % 1000) / 100);
    if (limit <= 10000 || total < 10000) return `${seconds}.${tenths}s`;
    return `${seconds}s`;
  };

  // Map remaining time to a pulse-tier class. Tiers cap based on the per-move
  // budget so a 5s blitz mode stays distinct from a 30s classical countdown.
  const tierFor = (remaining) => {
    const r = Math.max(0, remaining);
    if (r < 2000) return 'critical';
    if (r < 5000) return 'urgent';
    if (r < Math.min(10000, limit * 0.66)) return 'warning';
    return 'calm';
  };

  const p1Name = players?.player1?.displayName ?? 'P1';
  const p2Name = players?.player2?.displayName ?? 'P2';
  const labelP1 = yourPlayerNum === 1 ? `${p1Name} (you)` : p1Name;
  const labelP2 = yourPlayerNum === 2 ? `${p2Name} (you)` : p2Name;

  const cell = (player, label) => {
    const remaining = renderRemaining(player);
    const isActive = clock.active === player && !gameOver;
    const tier = isActive ? tierFor(remaining) : null;
    const classes = ['game-clock'];
    if (isActive) classes.push('game-clock--active');
    if (tier) classes.push(`game-clock--${tier}`);
    return (
      <div className={classes.join(' ')}>
        <span className="game-clock__label">{label}</span>
        <span className="game-clock__time">{fmt(remaining)}</span>
      </div>
    );
  };

  return (
    <div className="game-clocks" aria-live="polite">
      {cell(1, labelP1)}
      <span className="game-clocks__divider">{TIME_CONTROL_SHORT_LABELS[timeControl] || ''}</span>
      {cell(2, labelP2)}
    </div>
  );
}

// ── Jump preview helpers (shared with App.js) ──────────────────

const emptyJumpPreview = { jumpedPieces: [], usageCounts: {}, invalidIndex: -1, isValid: false };

const cloneBoard = (board) => board?.map?.(row => row.slice()) ?? [];

const validateJumpSegment = (board, start, end) => {
  if (!Array.isArray(start) || !Array.isArray(end)) return { valid: false, jumped: [] };
  const [sr, sc] = start;
  const [er, ec] = end;
  const dr = er - sr;
  const dc = ec - sc;
  if (!(dr === 0 || dc === 0 || Math.abs(dr) === Math.abs(dc))) return { valid: false, jumped: [] };
  const rStep = Math.sign(dr);
  const cStep = Math.sign(dc);
  if (rStep === 0 && cStep === 0) return { valid: false, jumped: [] };
  let r = sr + rStep;
  let c = sc + cStep;
  const jumped = [];
  while (!(r === er && c === ec)) {
    if (board?.[r]?.[c] !== TILE_TYPE.PHUTBALL_MAN) return { valid: false, jumped: [] };
    jumped.push([r, c]);
    r += rStep;
    c += cStep;
  }
  const landingTile = board?.[er]?.[ec];
  const isValidLanding = landingTile === TILE_TYPE.EMPTY || landingTile === TILE_TYPE.END_HI || landingTile === TILE_TYPE.END_LO;
  if (jumped.length === 0 || landingTile === undefined || !isValidLanding) return { valid: false, jumped: [] };
  return { valid: true, jumped };
};

// Geometric-only jump preview. No board validation — used when we know
// the jump is legal (e.g., previewing a historical move from the tree)
// but the snapshot board would confuse evaluateJumpSequence (we move
// the PHUTBALL token to the previewed parent's cell, which then makes
// landing-cell checks fail). Mirrors App.js's buildPreviewFromJumpPath.
const buildPreviewFromJumpPath = (start, path) => {
  if (!Array.isArray(start) || !Array.isArray(path) || path.length === 0) {
    return emptyJumpPreview;
  }
  const usageCounts = {};
  usageCounts[`${start[0]},${start[1]}`] = 1;
  const jumpedPieces = [];
  const seen = new Set();
  let current = start;
  path.forEach(target => {
    if (!Array.isArray(target)) return;
    usageCounts[`${target[0]},${target[1]}`] = (usageCounts[`${target[0]},${target[1]}`] ?? 0) + 1;
    const dr = target[0] - current[0];
    const dc = target[1] - current[1];
    const steps = Math.max(Math.abs(dr), Math.abs(dc));
    if (steps > 1) {
      const rStep = Math.sign(dr);
      const cStep = Math.sign(dc);
      for (let i = 1; i < steps; i += 1) {
        const r = current[0] + i * rStep;
        const c = current[1] + i * cStep;
        const key = `${r},${c}`;
        if (!seen.has(key)) { seen.add(key); jumpedPieces.push([r, c]); }
      }
    }
    current = target;
  });
  return { jumpedPieces, usageCounts, invalidIndex: -1, isValid: true };
};

const evaluateJumpSequence = (board, startPos, path) => {
  if (!Array.isArray(board) || board.length === 0 || !Array.isArray(startPos)) return emptyJumpPreview;
  const boardCopy = cloneBoard(board);
  const usageCounts = {};
  usageCounts[`${startPos[0]},${startPos[1]}`] = 1;
  let invalidIndex = -1;
  let current = startPos;
  let isValid = true;
  const jumpedPieces = [];
  path.forEach((target, idx) => {
    if (!Array.isArray(target)) { if (invalidIndex === -1) { invalidIndex = idx; isValid = false; } return; }
    if (invalidIndex === -1) {
      const { valid, jumped } = validateJumpSegment(boardCopy, current, target);
      if (valid) {
        jumpedPieces.push(...jumped);
        boardCopy[current[0]][current[1]] = TILE_TYPE.EMPTY;
        jumped.forEach(([jr, jc]) => { boardCopy[jr][jc] = TILE_TYPE.EMPTY; });
        boardCopy[target[0]][target[1]] = TILE_TYPE.PHUTBALL;
      } else { invalidIndex = idx; isValid = false; }
    }
    usageCounts[`${target[0]},${target[1]}`] = (usageCounts[`${target[0]},${target[1]}`] ?? 0) + 1;
    current = target;
  });
  if (path.length > 0 && invalidIndex === -1) isValid = true;
  return { jumpedPieces, usageCounts, invalidIndex, isValid };
};

// ── History builder ─────────────────────────────────────────────

const buildHistoryFromEngine = (engine) => {
  const moves = engine.getMoveHistory(); // [{ type, path, player }, ...]

  // Replay moves on a scratch engine to capture board text after each move
  const scratch = new PhutballEngine(engine.rows, engine.cols);

  // Linear history strings
  const history = ['Game Start'];

  // Log history for LogPanel
  const logHistory = [];

  // Per-tree-node snapshots so tree-view selection and hover-preview can
  // reconstruct the exact state at any point in the game (including
  // mid-jump-sequence) without having to re-replay moves.
  const snapshots = new Map();

  // Tree: build a linear chain of nodes
  const rootNode = {
    id: '0-root',
    moveSummary: 'start',
    moveDesc: 'Game Start',
    player: null,
    statePlayer: 1,
    isJump: false,
    isHalt: false,
    turn: 0,
    parentId: null,
    children: [],
  };
  snapshots.set(rootNode.id, scratch.getSnapshot());

  let prevNode = rootNode;
  let nodeUid = 0;

  moves.forEach((move, i) => {
    const { type, path, player } = move;
    const moveNum = i + 1;
    const phutballPosBefore = [...scratch.phutballPos];

    let historyStr;
    if (type === 'placement' && path.length > 0) {
      const pos = `(${path[0][0]}, ${path[0][1]})`;
      historyStr = `${moveNum}. P${player}: Place at ${pos}`;
    } else if (type === 'jump' && path.length > 0) {
      const segments = [phutballPosBefore, ...path].map(p => `(${p[0]}, ${p[1]})`);
      historyStr = `${moveNum}. P${player}: Jump ${segments.join(' -> ')}`;
    } else {
      historyStr = `${moveNum}. P${player}: Move`;
    }
    history.push(historyStr);

    if (type === 'placement' && path.length > 0) {
      scratch.applyPlacement(path[0]);
      const pos = `(${path[0][0]}, ${path[0][1]})`;
      nodeUid += 1;
      const node = {
        id: `${nodeUid}-P${player}-place-${pos}`,
        moveSummary: `P${player}-place-${pos}`,
        moveDesc: historyStr,
        player,
        statePlayer: 3 - player,
        isJump: false,
        isHalt: false,
        turn: moveNum,
        parentId: prevNode.id,
        children: [],
        // Atomic move metadata used by tree-view hover-preview without
        // having to parse moveDesc back out.
        atomicMove: { type: 'placement', position: [path[0][0], path[0][1]] },
      };
      prevNode.children.push(node);
      snapshots.set(node.id, scratch.getSnapshot());
      prevNode = node;
    } else if (type === 'jump' && path.length > 0) {
      // Decompose multi-hop jumps into one tree node per hop plus a
      // terminal halt node, matching the local-game tree structure
      // from App.js. Without this, every multi-hop jump showed up as
      // a single tree node and per-hop branching was unreachable.
      let prev = phutballPosBefore;
      let terminated = false;
      for (let hopIdx = 0; hopIdx < path.length; hopIdx += 1) {
        const target = path[hopIdx];
        const hopResult = scratch.applyJump(target);
        if (!hopResult.success) { terminated = true; break; }
        const hopDesc = `${moveNum}. P${player}: Jump (${prev[0]}, ${prev[1]}) -> (${target[0]}, ${target[1]})`;
        nodeUid += 1;
        const hopNode = {
          id: `${nodeUid}-P${player}-jump-(${prev[0]},${prev[1]})-(${target[0]},${target[1]})`,
          moveSummary: `P${player}-jump-(${prev[0]}, ${prev[1]})-(${target[0]}, ${target[1]})`,
          moveDesc: hopDesc,
          player,
          statePlayer: player,
          isJump: true,
          isHalt: false,
          turn: moveNum,
          parentId: prevNode.id,
          children: [],
          atomicMove: { type: 'jump', start: [prev[0], prev[1]], target: [target[0], target[1]] },
        };
        prevNode.children.push(hopNode);
        snapshots.set(hopNode.id, scratch.getSnapshot());
        prevNode = hopNode;
        prev = target;
        if (hopResult.terminated) { terminated = true; break; }
      }
      if (!terminated && scratch.winner === 0) {
        scratch.applyHalt();
        const haltDesc = `${moveNum}. P${player}: Halt jump sequence`;
        nodeUid += 1;
        const haltNode = {
          id: `${nodeUid}-P${player}-halt`,
          moveSummary: `P${player}-halt`,
          moveDesc: haltDesc,
          player,
          statePlayer: 3 - player,
          isJump: false,
          isHalt: true,
          turn: moveNum,
          parentId: prevNode.id,
          children: [],
          atomicMove: { type: 'halt' },
        };
        prevNode.children.push(haltNode);
        snapshots.set(haltNode.id, scratch.getSnapshot());
        prevNode = haltNode;
      }
    } else {
      scratch.applyMove(move);
    }

    logHistory.push({
      turnIndex: moveNum,
      player,
      actions: [{
        moveDesc: historyStr,
        boardText: scratch.textRender(player),
        reasoning: null,
        thinkingTime: null,
        tokenUsage: null,
      }],
      turnReasoning: null,
    });
  });

  const liveNodeId = prevNode.id;

  return {
    history,
    historyIndex: history.length - 1,
    logHistory,
    historyTree: {
      root: rootNode,
      currentNodeId: liveNodeId,
      liveNodeId,
    },
    snapshots,
  };
};

// ── Layout helpers ──────────────────────────────────────────────

const PANEL_CONFIG = [
  { id: 'log', label: 'Chat & Log', shortLabel: 'Chat' },
  { id: 'board', label: 'Game Board', shortLabel: 'Board' },
  { id: 'history', label: 'History', shortLabel: 'History' },
];

const shouldUseStackedLayout = () => {
  if (typeof window === 'undefined') return false;
  const { innerWidth, innerHeight } = window;
  const w = typeof innerWidth === 'number' ? innerWidth : window.screen?.width ?? 0;
  const h = typeof innerHeight === 'number' ? innerHeight : window.screen?.height ?? 0;
  if (!w || !h) return w <= 1200;
  return w <= 1200 || h > w;
};

const clampPanelIndex = (value) => Math.max(0, Math.min(PANEL_CONFIG.length - 1, value));

const POINTER_SWIPE_FRACTION = 0.3;
const MIN_SWIPE_DISTANCE = 120;

const computeSwipeThreshold = (element, fraction = POINTER_SWIPE_FRACTION) => {
  const width = element?.clientWidth ?? window.innerWidth ?? document.documentElement?.clientWidth ?? 0;
  const fractionTarget = width > 0 ? width * fraction : 0;
  return Math.max(MIN_SWIPE_DISTANCE, fractionTarget);
};

// ── Reconnection config ─────────────────────────────────────────
const RECONNECT_INTERVAL = 3000;
const RECONNECT_TIMEOUT = 5 * 60 * 1000; // 5 minutes

// ── Component ───────────────────────────────────────────────────

const OnlineGame = ({ gameId, onExit }) => {
  const { user, token } = useAuth();
  const socketRef = useRef(null);
  const engineRef = useRef(null);
  // Per-tree-node snapshots so the right-panel tree view can scrub by
  // node id (mid-jump-sequence states included), not just by atomic
  // move index. Refilled on every game_state and move_result message.
  const nodeSnapshotsRef = useRef(new Map());

  const [status, setStatus] = useState('Connecting...');
  const [gameState, setGameState] = useState(null);
  const [yourPlayerNum, setYourPlayerNum] = useState(null);
  const [players, setPlayers] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [gameOver, setGameOver] = useState(null);
  const [rematchOffered, setRematchOffered] = useState(false);
  const [rematchRequested, setRematchRequested] = useState(false);
  const [opponentConnected, setOpponentConnected] = useState(true);
  const [timeControl, setTimeControl] = useState('live');
  const [clock, setClockRaw] = useState(null);
  const [clockReceivedAt, setClockReceivedAt] = useState(null);
  const [clockStarted, setClockStarted] = useState(false);
  // Wrap setClock so every clock update also captures the local arrival
  // timestamp the rAF loop counts down from.
  const setClock = useCallback((next) => {
    setClockRaw(next);
    setClockReceivedAt(next ? Date.now() : null);
  }, []);
  const [timeouts, setTimeouts] = useState({ p1: 0, p2: 0 });
  const gameStartAtRef = useRef(null);
  const gameCompleteSentRef = useRef(false);

  // Fire game_complete telemetry once per online game.
  useEffect(() => {
    if (!gameOver || gameCompleteSentRef.current) return;
    gameCompleteSentRef.current = true;
    const startedAt = gameStartAtRef.current || Date.now();
    const winnerNum = gameOver.winner === 'player1' ? 1
      : gameOver.winner === 'player2' ? 2
      : null;
    trackEvent('game_complete', {
      mode: 'online',
      time_control: timeControl,
      rows: gameState?.board?.length ?? null,
      cols: gameState?.board?.[0]?.length ?? null,
      winner: winnerNum,
      reason: gameOver.reason || null,
      turn_count: gameState?.history?.length ? gameState.history.length - 1 : 0,
      duration_ms: Date.now() - startedAt,
      timeouts_p1: timeouts.p1 || 0,
      timeouts_p2: timeouts.p2 || 0,
      timeouts_total: (timeouts.p1 || 0) + (timeouts.p2 || 0),
    });
  }, [gameOver, timeControl, gameState, timeouts]);

  const [isPauseMenuOpen, setIsPauseMenuOpen] = useState(false);
  const [coordSystem, setCoordSystem] = useState('endzone');
  const [showMinimap, setShowMinimap] = useState(true);
  const boardControlsRef = useRef(null);
  const [historyView, setHistoryView] = useState('linear');
  const [hoverPos, setHoverPos] = useState(null);
  const [cursorPos, setCursorPos] = useState(null);
  const [awaitingAck, setAwaitingAck] = useState(false);

  // History viewing — null means live, number means viewing that history index
  const [viewingIndex, setViewingIndex] = useState(null);
  // Specific tree node being viewed (overrides viewingIndex when set so
  // mid-sequence hop nodes can be inspected with their own state).
  const [viewingNodeId, setViewingNodeId] = useState(null);
  // Tree-edge hover preview state — board snapshot the user's pointer is
  // currently inspecting on the tree. Null when nothing is being hovered.
  const [historyPreviewState, setHistoryPreviewState] = useState(null);
  const [previewedTreeEdge, setPreviewedTreeEdge] = useState({ parentId: null, childId: null });
  const lastPreviewEdgeRef = useRef(null);
  const toggleJumpEntryRef = useRef(null);
  const registerToggleJumpEntry = useCallback((fn) => { toggleJumpEntryRef.current = fn; }, []);

  // Keyboard focus: 'board' or 'history'
  const [keyFocus, setKeyFocus] = useState('board');
  const [historyFocusIndex, setHistoryFocusIndex] = useState(0);

  // Move staging
  const [stagedMove, setStagedMove] = useState({ type: 'none', path: [] });

  // Stacked (mobile) layout
  const [isStackedLayout, setIsStackedLayout] = useState(() => shouldUseStackedLayout());
  const [activePanelIndex, setActivePanelIndex] = useState(1);
  const activePanelIndexRef = useRef(1);
  const swipeContainerRef = useRef(null);
  const [swipeOffsetPct, setSwipeOffsetPct] = useState(0);
  const [isPagerDragging, setIsPagerDragging] = useState(false);

  useEffect(() => { activePanelIndexRef.current = activePanelIndex; }, [activePanelIndex]);

  useEffect(() => {
    const handleResize = () => setIsStackedLayout(shouldUseStackedLayout());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Swipe gesture handling (matches App.js) ─────────────────
  const settlePagerOffset = useCallback(() => {
    setSwipeOffsetPct(0);
    setIsPagerDragging(false);
  }, []);

  useEffect(() => {
    if (!isStackedLayout) { settlePagerOffset(); return () => {}; }
    const container = swipeContainerRef.current;
    if (!container) return () => {};

    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let tracking = false;
    let requiredDistance = 0;

    const resetTracking = () => { pointerId = null; tracking = false; requiredDistance = 0; };

    const handlePointerDown = (event) => {
      if (!isStackedLayout || tracking) return;
      if (event.pointerType === 'mouse' && event.buttons !== 1) return;
      // Don't intercept events on the board area
      if (event.target?.closest('.board-layout-container')) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      requiredDistance = computeSwipeThreshold(container, POINTER_SWIPE_FRACTION);
      tracking = true;
      setIsPagerDragging(true);
      setSwipeOffsetPct(0);
    };

    const handlePointerMove = (event) => {
      if (!tracking || (pointerId != null && event.pointerId !== pointerId)) return;
      const dx = event.clientX - startX;
      const width = container.clientWidth || window.innerWidth || 1;
      let offsetPct = Math.max(-100, Math.min(100, (dx / width) * 100));
      const panelIdx = activePanelIndexRef.current;
      if (panelIdx === 0 && offsetPct > 0) offsetPct = 0;
      else if (panelIdx === PANEL_CONFIG.length - 1 && offsetPct < 0) offsetPct = 0;
      setSwipeOffsetPct(offsetPct);
    };

    const handlePointerUp = (event) => {
      if (!tracking || (pointerId != null && event.pointerId !== pointerId)) {
        resetTracking(); settlePagerOffset(); return;
      }
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      const threshold = requiredDistance > 0 ? requiredDistance : computeSwipeThreshold(container, POINTER_SWIPE_FRACTION);
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) >= threshold) {
        const direction = dx < 0 ? 1 : -1;
        resetTracking();
        setActivePanelIndex(prev => clampPanelIndex(prev + direction));
        settlePagerOffset();
        return;
      }
      resetTracking();
      settlePagerOffset();
    };

    const handlePointerCancel = () => { if (!tracking) return; resetTracking(); settlePagerOffset(); };

    container.addEventListener('pointerdown', handlePointerDown, { passive: true });
    container.addEventListener('pointermove', handlePointerMove, { passive: true });
    container.addEventListener('pointerup', handlePointerUp);
    container.addEventListener('pointercancel', handlePointerCancel);
    container.addEventListener('pointerleave', handlePointerCancel);
    return () => {
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', handlePointerUp);
      container.removeEventListener('pointercancel', handlePointerCancel);
      container.removeEventListener('pointerleave', handlePointerCancel);
    };
  }, [isStackedLayout, settlePagerOffset]);

  // ── WebSocket connection with reconnection ────────────────────
  const reconnectTimerRef = useRef(null);
  const disconnectedAtRef = useRef(null);
  const intentionalCloseRef = useRef(false);

  const connectWebSocket = useCallback(() => {
    if (!gameId || !token) return;

    const apiUrl = process.env.REACT_APP_API_URL || '';
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = apiUrl.replace(/^https?:\/\//, '') || window.location.host;
    const wsUrl = `${wsProtocol}://${wsHost}/game/${gameId}?token=${encodeURIComponent(token)}`;

    const ws = new WebSocket(wsUrl);
    socketRef.current = ws;

    ws.onopen = () => {
      setStatus('Connected');
      disconnectedAtRef.current = null;
      if (reconnectTimerRef.current) {
        clearInterval(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    ws.onclose = () => {
      if (intentionalCloseRef.current) {
        setStatus('Disconnected');
        return;
      }
      // Start reconnection attempts
      if (!disconnectedAtRef.current) {
        disconnectedAtRef.current = Date.now();
      }
      setStatus('Reconnecting...');

      if (!reconnectTimerRef.current) {
        reconnectTimerRef.current = setInterval(() => {
          const elapsed = Date.now() - (disconnectedAtRef.current || Date.now());
          if (elapsed > RECONNECT_TIMEOUT) {
            clearInterval(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
            setStatus('Disconnected (timeout)');
            return;
          }
          connectWebSocket();
        }, RECONNECT_INTERVAL);
      }
    };

    ws.onerror = () => {
      // onclose will handle reconnection
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        handleServerMessage(msg);
      } catch (err) {
        console.error('[OnlineGame] Parse error:', err);
      }
    };
  }, [gameId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    intentionalCloseRef.current = false;
    connectWebSocket();
    return () => {
      intentionalCloseRef.current = true;
      if (reconnectTimerRef.current) {
        clearInterval(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const ws = socketRef.current;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close();
      }
    };
  }, [connectWebSocket]);

  const handleServerMessage = useCallback((msg) => {
    switch (msg.type) {
      case 'game_state': {
        const { state } = msg;
        const rows = state.board.length;
        const cols = state.board[0]?.length || 15;
        const engine = new PhutballEngine(rows, cols);
        if (state.moves && state.moves.length > 0) {
          engine.replayMoves(state.moves);
        }
        engineRef.current = engine;
        if (gameStartAtRef.current == null) {
          gameStartAtRef.current = Date.now();
        }

        const { history, historyIndex, historyTree, logHistory, snapshots } = buildHistoryFromEngine(engine);
        nodeSnapshotsRef.current = snapshots;
        setGameState({
          board: state.board,
          currentPlayer: state.currentPlayer,
          phutballPos: state.phutballPos,
          winner: state.winner,
          numTurns: state.numTurns,
          stateHash: state.stateHash,
          history,
          historyIndex,
          historyTree,
          logHistory,
        });
        setTimeControl(state.timeControl || 'live');
        setClock(state.clock || null);
        setClockStarted(!!state.clockStarted);
        if (state.timeouts) setTimeouts(state.timeouts);
        setYourPlayerNum(state.yourPlayerNum);
        setPlayers(state.players);
        setChatHistory(state.chatHistory || []);
        setCursorPos(state.phutballPos);
        setStagedMove({ type: 'none', path: [] });
        if (state.gameOver) {
          setGameOver({ winner: state.winner > 0 ? (state.winner === 1 ? 'player1' : 'player2') : null });
        }
        break;
      }

      case 'move_result': {
        const engine = engineRef.current;
        if (!engine) break;

        // Tail effects fired after the visual update settles. Wrapped so the
        // jump animation can defer them until the last hop has been rendered.
        const finalizeMove = () => {
          if (msg.clock !== undefined) setClock(msg.clock);
          if (msg.autoMove) {
            setTimeouts(prev => ({
              ...prev,
              [`p${msg.autoMovePlayer}`]: (prev[`p${msg.autoMovePlayer}`] || 0) + 1,
            }));
          }
          setStagedMove({ type: 'none', path: [] });
          setAwaitingAck(false);
        };

        const isJump = msg.move?.type === 'jump' && Array.isArray(msg.move.path) && msg.move.path.length > 0;

        if (isJump) {
          // Step through each hop on a clone so the ball visibly moves and
          // consumed stones drop out one segment at a time. The real engine
          // is updated atomically at the end so moveHistory stays correct.
          // Runs for both your own moves and the opponent's: the trace was
          // already drawn during staging for your own jump but the ball
          // would otherwise snap straight to the landing cell.
          const animEngine = new PhutballEngine(engine.rows, engine.cols);
          animEngine.loadSnapshot(engine.getSnapshot());
          const path = msg.move.path;
          const HOP_DELAY_MS = 350;

          // Telegraph the upcoming jump path with the standard staged
          // overlay. startPos is left null so GameBoard renders the trace
          // anchored at phutballPos (which is updated each hop), giving
          // [currentBallPos, ...remainingHops]. Without this, revisited
          // cells in the path produced a broken line that jumped from the
          // original start straight to the next pending hop.
          setStagedMove({ type: 'jump', path: [...path], startPos: null });

          const playHop = (i) => {
            if (i >= path.length) {
              // Animation complete. Apply the move to the canonical engine
              // (records moveHistory + halts) and emit the final React state.
              engine.applyMove(msg.move);
              const final = buildHistoryFromEngine(engine);
              nodeSnapshotsRef.current = final.snapshots;
              setGameState({
                board: engine.board.map(r => [...r]),
                currentPlayer: engine.currentPlayer,
                phutballPos: [...engine.phutballPos],
                winner: engine.winner,
                numTurns: engine.numTurns,
                stateHash: msg.stateHash,
                ...final,
              });
              setCursorPos([...engine.phutballPos]);
              finalizeMove();
              return;
            }
            const hopResult = animEngine.applyJump(path[i]);
            // Mid-jump renders only update the visual board + ball pos; the
            // history pane stays put until the final atomic apply.
            setGameState(prev => prev ? {
              ...prev,
              board: animEngine.board.map(r => [...r]),
              phutballPos: [...animEngine.phutballPos],
            } : prev);
            setCursorPos([...animEngine.phutballPos]);
            // Drop the segment we just travelled from the staged overlay
            // so the path appears to be "eaten" from start to end.
            setStagedMove(prev => prev?.type === 'jump'
              ? { ...prev, path: prev.path.slice(1) }
              : prev);
            // Scoring hops terminate the sequence early.
            if (hopResult.terminated) {
              setTimeout(() => playHop(path.length), HOP_DELAY_MS);
            } else {
              setTimeout(() => playHop(i + 1), HOP_DELAY_MS);
            }
          };
          playHop(0);
        } else {
          // Placement or halt — no hop sequence to animate.
          engine.applyMove(msg.move);
          const { history, historyIndex, historyTree, logHistory, snapshots } = buildHistoryFromEngine(engine);
          nodeSnapshotsRef.current = snapshots;
          setGameState({
            board: engine.board.map(r => [...r]),
            currentPlayer: engine.currentPlayer,
            phutballPos: [...engine.phutballPos],
            winner: engine.winner,
            numTurns: engine.numTurns,
            stateHash: msg.stateHash,
            history,
            historyIndex,
            historyTree,
            logHistory,
          });
          setCursorPos([...engine.phutballPos]);
          finalizeMove();
        }
        break;
      }

      case 'chat_message':
        setChatHistory(prev => [...prev, {
          userId: msg.userId, displayName: msg.displayName,
          message: msg.message, afterMove: msg.afterMove, timestamp: msg.timestamp,
        }]);
        break;

      case 'game_over':
        if (msg.clock !== undefined) setClock(msg.clock);
        if (msg.timeouts) setTimeouts(msg.timeouts);
        setGameOver({ winner: msg.winner, reason: msg.reason, elo: msg.elo || null });
        try { sessionStorage.removeItem('phutball_pending_online_game'); } catch {}
        break;

      case 'clock_started':
        if (msg.clock !== undefined) setClock(msg.clock);
        setClockStarted(true);
        break;

      case 'opponent_disconnected':
        setOpponentConnected(false);
        break;

      case 'opponent_reconnected':
        setOpponentConnected(true);
        break;

      case 'rematch_offer':
        setRematchOffered(true);
        break;

      case 'rematch_accepted':
        window.location.hash = `#game=${msg.newGameId}`;
        break;

      case 'error':
        console.warn('[OnlineGame] Error:', msg.message);
        setAwaitingAck(false);
        break;
    }
  }, []);

  const sendAction = useCallback((action) => {
    const ws = socketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(action));
      return true;
    }
    return false;
  }, []);

  // Flat lookup of every tree node by id, refreshed when the tree changes.
  const nodesById = useMemo(() => {
    const map = new Map();
    const traverse = (node) => {
      if (!node || !node.id) return;
      map.set(node.id, node);
      (node.children ?? []).forEach(traverse);
    };
    if (gameState?.historyTree?.root) traverse(gameState.historyTree.root);
    return map;
  }, [gameState?.historyTree]);
  // Mirror nodesById through a ref so handlePreviewTreeEdge can stay
  // identity-stable. Without this, every game_state message changed
  // nodesById, which changed handlePreviewTreeEdge, which changed
  // TreeHistory's applyPreviewEdge useCallback, which fired its
  // cleanup effect — calling applyPreviewEdge(null) → my handler →
  // setHistoryPreviewState(null) → re-render → repeat. Manifested as
  // React error #185 (Maximum update depth) on tree-edge hover.
  const nodesByIdRef = useRef(nodesById);
  useEffect(() => { nodesByIdRef.current = nodesById; }, [nodesById]);

  const liveNodeIdValue = gameState?.historyTree?.liveNodeId ?? null;
  const treePreviewActive = historyPreviewState?.source === 'tree';
  // Prefer the tree-hover preview snapshot, then a tree-selected node's
  // snapshot, then the legacy turn-index replay. viewingNodeId targets a
  // specific tree node so per-hop scrubbing actually shows the per-hop
  // state instead of collapsing to the parent turn's atomic snapshot.
  const viewedSnapshot = useMemo(() => {
    if (historyPreviewState?.board && historyPreviewState?.phutballPos) {
      return {
        board: historyPreviewState.board,
        phutballPos: historyPreviewState.phutballPos,
        currentPlayer: historyPreviewState.currentPlayer,
      };
    }
    if (viewingNodeId && nodeSnapshotsRef.current.has(viewingNodeId)) {
      const snap = nodeSnapshotsRef.current.get(viewingNodeId);
      return {
        board: snap.board.map(r => [...r]),
        phutballPos: [...snap.phutballPos],
        currentPlayer: snap.currentPlayer,
      };
    }
    return null;
  }, [historyPreviewState, viewingNodeId, nodesById]); // nodesById dep refreshes the snapshot map link

  const isYourTurn = gameState && yourPlayerNum === gameState.currentPlayer && !gameOver;
  const isViewingHistory = Boolean(viewedSnapshot)
    || (viewingIndex !== null && gameState && viewingIndex < (gameState.history?.length ?? 0) - 1);
  const canAct = isYourTurn && !awaitingAck && !isViewingHistory;

  const gameStats = useMemo(() => {
    const history = gameState?.history ?? [];
    if (history.length <= 1) return null;
    const rows = gameState?.board?.length ?? 21;
    const midRow = Math.floor(rows / 2);

    const stats = {
      p1: { jumpSequences: [], totalPiecesJumped: 0, totalJumps: 0 },
      p2: { jumpSequences: [], totalPiecesJumped: 0, totalJumps: 0 },
      possession: { p1Turns: 0, p2Turns: 0 },
    };

    let currentBallRow = midRow;
    let ballHasMoved = false;

    for (const entry of history) {
      if (!entry || entry === 'Game Start') continue;
      const playerMatch = entry.match(/P(\d):/);
      if (!playerMatch) continue;
      const player = parseInt(playerMatch[1], 10);
      const pKey = player === 1 ? 'p1' : 'p2';

      const isJump = entry.includes('Jump');
      if (isJump) {
        const arrowCount = (entry.match(/->/g) || []).length;
        if (arrowCount > 0) {
          stats[pKey].jumpSequences.push(arrowCount);
          stats[pKey].totalJumps += arrowCount;
          stats[pKey].totalPiecesJumped += arrowCount;
        }
        const posMatch = entry.match(/\((-?\d+),\s*(-?\d+)\)/g);
        if (posMatch && posMatch.length > 0) {
          const lastPos = posMatch[posMatch.length - 1];
          const rowMatch = lastPos.match(/\((-?\d+)/);
          if (rowMatch) {
            currentBallRow = parseInt(rowMatch[1], 10);
            ballHasMoved = true;
          }
        }
      }

      if (ballHasMoved) {
        if (currentBallRow < midRow) stats.possession.p2Turns++;
        else if (currentBallRow > midRow) stats.possession.p1Turns++;
      }
    }

    const p1Seqs = stats.p1.jumpSequences;
    const p2Seqs = stats.p2.jumpSequences;
    const result = {
      p1: {
        jumpSequenceCount: p1Seqs.length,
        avgJumpsPerSeq: p1Seqs.length > 0 ? p1Seqs.reduce((a, b) => a + b, 0) / p1Seqs.length : undefined,
        avgPiecesPerJump: stats.p1.totalJumps > 0 ? stats.p1.totalPiecesJumped / stats.p1.totalJumps : undefined,
      },
      p2: {
        jumpSequenceCount: p2Seqs.length,
        avgJumpsPerSeq: p2Seqs.length > 0 ? p2Seqs.reduce((a, b) => a + b, 0) / p2Seqs.length : undefined,
        avgPiecesPerJump: stats.p2.totalJumps > 0 ? stats.p2.totalPiecesJumped / stats.p2.totalJumps : undefined,
      },
      possession: null,
    };

    const totalPossTurns = stats.possession.p1Turns + stats.possession.p2Turns;
    if (totalPossTurns > 0) {
      result.possession = {
        p1: Math.round((stats.possession.p1Turns / totalPossTurns) * 100),
        p2: Math.round((stats.possession.p2Turns / totalPossTurns) * 100),
      };
    }

    // The Workers backend doesn't emit pieceTilt or statsHistory like the
    // Python server does, so derive them client-side by walking the live
    // linear chain of tree nodes and reading the per-node snapshots that
    // buildHistoryFromEngine already captured.
    const liveId = gameState?.historyTree?.liveNodeId ?? null;
    const snapshots = nodeSnapshotsRef.current;
    if (liveId && nodesById.size > 0 && snapshots) {
      const chain = [];
      let curId = liveId;
      while (curId) {
        const n = nodesById.get(curId);
        if (!n) break;
        chain.unshift(n);
        curId = n.parentId;
      }

      const center = (rows - 1) / 2;
      const maxDist = center > 0 ? center : 1;
      const MIDLINE = 0.06;

      const statsHistory = [];
      let tiltP1 = 0, tiltP2 = 0;

      chain.forEach((node, i) => {
        const snap = snapshots.get(node.id);
        if (!snap) return;
        const ballRow = snap.phutballPos?.[0];
        const fieldPosition = (ballRow != null && Number.isFinite(ballRow))
          ? (ballRow - center) / maxDist
          : null;

        let pieceTilt = null;
        const board = snap.board;
        if (Array.isArray(board)) {
          let sum = 0, count = 0;
          for (let r = 0; r < board.length; r += 1) {
            const row = board[r];
            if (!Array.isArray(row)) continue;
            for (let c = 0; c < row.length; c += 1) {
              if (row[c] === TILE_TYPE.PHUTBALL_MAN) { sum += r; count += 1; }
            }
          }
          if (count > 0) pieceTilt = (sum / count - center) / maxDist;
        }

        if (pieceTilt !== null) {
          if (pieceTilt < -MIDLINE) tiltP1 += 1;
          else if (pieceTilt > MIDLINE) tiltP2 += 1;
        }

        statsHistory.push({
          moveIndex: i,
          nodeId: node.id,
          fieldPosition,
          pieceTilt,
          value: null,
        });
      });

      result.statsHistory = statsHistory;
      const tiltTotal = tiltP1 + tiltP2;
      if (tiltTotal > 0) {
        result.pieceTilt = {
          p1: Math.round(100 * tiltP1 / tiltTotal),
          p2: Math.round(100 * tiltP2 / tiltTotal),
        };
      } else {
        result.pieceTilt = null;
      }
    } else {
      result.pieceTilt = null;
      result.statsHistory = [];
    }
    return result;
  }, [gameState?.history, gameState?.board?.length, gameState?.historyTree, nodesById]);

  // ── History viewing: compute board at viewed index ─────────────
  const viewedBoard = useMemo(() => {
    if (viewedSnapshot) return viewedSnapshot;
    if (!isViewingHistory || !engineRef.current) return null;
    const engine = engineRef.current;
    const scratch = new PhutballEngine(engine.rows, engine.cols);
    const moves = engine.getMoveHistory();
    if (viewingIndex > 0) {
      scratch.replayMoves(moves.slice(0, viewingIndex));
    }
    return {
      board: scratch.board.map(r => [...r]),
      phutballPos: [...scratch.phutballPos],
    };
  }, [viewedSnapshot, isViewingHistory, viewingIndex, gameState?.historyIndex]);

  const handleHistoryClick = useCallback((index) => {
    if (!gameState) return;
    const liveIndex = (gameState.history?.length ?? 1) - 1;
    setViewingNodeId(null);
    if (index >= liveIndex) {
      setViewingIndex(null);
    } else {
      setViewingIndex(index);
    }
  }, [gameState]);

  // Read live values through refs so the callback can stay identity-stable
  // — same reasoning as handlePreviewTreeEdge (TreeHistory's
  // applyPreviewEdge cleanup fires whenever its callback identity flips).
  const liveNodeIdRef = useRef(liveNodeIdValue);
  useEffect(() => { liveNodeIdRef.current = liveNodeIdValue; }, [liveNodeIdValue]);
  const historyLengthRef = useRef(0);
  useEffect(() => { historyLengthRef.current = gameState?.history?.length ?? 0; }, [gameState?.history?.length]);

  const handleSelectTreeNode = useCallback((nodeId) => {
    if (!nodeId) return;
    const lookup = nodesByIdRef.current;
    if (!lookup.has(nodeId)) return;
    const node = lookup.get(nodeId);
    setHistoryFocusIndex(node.turn);
    setHistoryPreviewState(null);
    setPreviewedTreeEdge({ parentId: null, childId: null });
    lastPreviewEdgeRef.current = null;
    setStagedMove({ type: 'none', path: [] });
    if (nodeId === liveNodeIdRef.current) {
      setViewingIndex(null);
      setViewingNodeId(null);
      return;
    }
    setViewingNodeId(nodeId);
    // Keep viewingIndex in sync with the node's logical turn so anything
    // that still reads viewingIndex (linear list highlight, log panel,
    // etc.) shows the right turn while the snapshot drives the board.
    const liveIndex = Math.max(0, historyLengthRef.current - 1);
    setViewingIndex(node.turn >= liveIndex ? null : node.turn);
  }, []);

  const handleGoLive = useCallback(() => {
    setViewingIndex(null);
    setViewingNodeId(null);
    setHistoryPreviewState(null);
    setPreviewedTreeEdge({ parentId: null, childId: null });
    lastPreviewEdgeRef.current = null;
    setStagedMove({ type: 'none', path: [] });
  }, []);

  const handlePreviewTreeEdge = useCallback((edge) => {
    const key = edge ? `${edge.from?.id}-${edge.to?.id}` : null;
    if (lastPreviewEdgeRef.current === key) return;
    lastPreviewEdgeRef.current = key;
    if (!edge) {
      setHistoryPreviewState(null);
      setPreviewedTreeEdge({ parentId: null, childId: null });
      setStagedMove({ type: 'none', path: [] });
      return;
    }
    const parentId = edge.from?.id ?? null;
    const childId = edge.to?.id ?? null;
    const lookup = nodesByIdRef.current;
    const childNode = childId ? lookup.get(childId) : null;
    if (!childNode) return;

    const parentSnapshot = nodeSnapshotsRef.current.get(parentId);
    if (!parentSnapshot) return;

    let preview = null;
    let previewBoard = parentSnapshot.board.map(row => [...row]);
    let previewPhutballPos = [...parentSnapshot.phutballPos];

    if (childNode.atomicMove?.type === 'placement') {
      preview = { type: 'placement', path: [childNode.atomicMove.position] };
    } else if (childNode.isJump) {
      // Walk back to the pre-sequence node (the placement/halt before
      // the jump chain). Use its snapshot's BOARD so every stone the
      // sequence consumed is still visible — without that the trace
      // looks like an illegal jump (no stones to leap over). But
      // override the ball position with the parent hop's snapshot so
      // adjacent hovers still glide one hop instead of yo-yoing back to
      // the sequence start.
      const jumpNodes = [];
      let cur = childNode;
      while (cur && (cur.isJump || cur.isHalt)) {
        if (cur.isJump) jumpNodes.unshift(cur);
        cur = cur.parentId ? lookup.get(cur.parentId) : null;
      }
      const preSequenceNode = cur; // first non-jump-non-halt ancestor
      const preSeqSnapshot = preSequenceNode
        ? nodeSnapshotsRef.current.get(preSequenceNode.id)
        : null;
      if (preSeqSnapshot) {
        const board = preSeqSnapshot.board.map(row => [...row]);
        const [psR, psC] = preSeqSnapshot.phutballPos;
        const [pR, pC] = parentSnapshot.phutballPos;
        if (psR !== pR || psC !== pC) {
          board[psR][psC] = TILE_TYPE.EMPTY;
          board[pR][pC] = TILE_TYPE.PHUTBALL;
        }
        previewBoard = board;
        previewPhutballPos = [pR, pC];
      }
      if (jumpNodes.length > 0) {
        const start = jumpNodes[0].atomicMove?.start ?? null;
        const path = jumpNodes
          .map(n => n.atomicMove?.target)
          .filter(Boolean);
        if (start && path.length > 0) preview = { type: 'jump', path, start };
      }
    }
    // Halt nodes have no movement to preview — fall through with
    // preview = null and the parent's snapshot.

    setPreviewedTreeEdge({ parentId, childId });
    setHistoryPreviewState({
      source: 'tree',
      board: previewBoard,
      phutballPos: previewPhutballPos,
      currentPlayer: parentSnapshot.currentPlayer,
      nodeId: childId,
      parentNodeId: parentId,
    });
    if (preview?.type === 'jump') {
      setStagedMove({ type: 'jump', path: preview.path.map(p => [...p]), startPos: [...preview.start] });
    } else if (preview?.type === 'placement') {
      setStagedMove({ type: 'placement', path: [[...preview.path[0]]] });
    } else {
      setStagedMove({ type: 'none', path: [] });
    }
  }, []);


  // ── Jump preview ──────────────────────────────────────────────
  // When hovering a tree node, validate against the preview snapshot's
  // board (which has the consumed stones restored), not the live game
  // state — otherwise the trace renders as illegal because the actual
  // game has already eaten the stones the historical jump leapt over.
  const jumpPreview = useMemo(() => {
    if (stagedMove.type !== 'jump') return emptyJumpPreview;
    // Tree-hover previews are real historical moves — known-legal — so
    // skip board validation and just compute the geometric trace.
    if (historyPreviewState && Array.isArray(stagedMove.startPos)) {
      return buildPreviewFromJumpPath(stagedMove.startPos, stagedMove.path);
    }
    const board = gameState?.board;
    const phutballPos = gameState?.phutballPos;
    if (!board || !phutballPos) return emptyJumpPreview;
    return evaluateJumpSequence(board, phutballPos, stagedMove.path);
  }, [stagedMove, gameState?.board, gameState?.phutballPos, historyPreviewState]);

  // ── Move staging handlers ─────────────────────────────────────
  const handlePlace = useCallback(() => {
    if (!canAct || !cursorPos) return;
    const [r, c] = cursorPos;
    const board = gameState?.board;
    if (!board || r <= 0 || r >= board.length - 1) return;
    if (board[r][c] !== TILE_TYPE.EMPTY) return;
    setStagedMove({ type: 'placement', path: [cursorPos] });
  }, [canAct, cursorPos, gameState]);

  const handleJump = useCallback(() => {
    if (!canAct || !cursorPos) return;
    const ballPos = gameState?.phutballPos;
    setStagedMove(prev => {
      if (prev.type === 'jump') {
        return { ...prev, path: [...prev.path, cursorPos] };
      }
      // Capture startPos = current ball cell so GameBoard's
      // ballAnchoredForStaging fires and the trace node at the ball's
      // cell paints on top of the ball — including the visit-count
      // indicator if the planned path returns to start.
      return {
        type: 'jump',
        path: [cursorPos],
        startPos: Array.isArray(ballPos) ? [...ballPos] : null,
      };
    });
  }, [canAct, cursorPos, gameState]);

  const handleUndo = useCallback(() => {
    setStagedMove(prev => {
      if (prev.type === 'jump' && prev.path.length > 1) {
        return { ...prev, path: prev.path.slice(0, -1) };
      }
      return { type: 'none', path: [] };
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (!canAct) return;
    if (stagedMove.type === 'placement' && stagedMove.path.length === 1) {
      setAwaitingAck(true);
      sendAction({ action: 'move', move: { type: 'placement', path: stagedMove.path } });
    } else if (stagedMove.type === 'jump' && stagedMove.path.length > 0 && jumpPreview.isValid) {
      setAwaitingAck(true);
      sendAction({ action: 'move', move: { type: 'jump', path: stagedMove.path } });
    }
  }, [canAct, stagedMove, jumpPreview.isValid, sendAction]);

  // ── Cell click: placement or jump waypoint ────────────────────
  const handleCellClick = useCallback((row, col) => {
    if (!canAct) return;
    setCursorPos([row, col]);

    const board = gameState?.board;
    if (!board) return;
    const tile = board[row]?.[col];

    // If we're building a jump, add waypoint
    if (stagedMove.type === 'jump') {
      setStagedMove(prev => ({ ...prev, path: [...prev.path, [row, col]] }));
      return;
    }

    // Click on empty = stage placement; click elsewhere does nothing special
    const canPlace = tile === TILE_TYPE.EMPTY && row > 0 && row < board.length - 1;
    if (canPlace) {
      setStagedMove({ type: 'placement', path: [[row, col]] });
    }
  }, [canAct, gameState, stagedMove.type]);

  // ── Keyboard navigation ───────────────────────────────────────
  // Panel focus flash animation
  const [panelFlash, setPanelFlash] = useState(null);
  const flashPanel = useCallback((idx) => {
    setPanelFlash(idx);
    setTimeout(() => setPanelFlash(null), 400);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key;
      const lower = key.toLowerCase();

      // Don't intercept when typing in an input or textarea (except panel cycling + Escape)
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') {
        if (key === 'Escape') {
          document.activeElement.blur();
          e.preventDefault();
          return;
        }
        // Allow Shift+A/D/Arrows to cycle panels even from an input
        const isShiftNav = e.shiftKey && (key === 'ArrowLeft' || key === 'ArrowRight' || lower === 'a' || lower === 'd');
        if (!isShiftNav) return;
        document.activeElement.blur();
      }

      // Panel cycling: Shift+A/Left moves focus left, Shift+D/Right moves
      // focus right. Three panels: chat(0) ← board(1) → history(2). Mirrors
      // the local-game two-state toggle but with chat included on the left.
      const focusOrder = ['chat', 'board', 'history'];
      const wantsLeft = e.shiftKey && (key === 'ArrowLeft' || lower === 'a');
      const wantsRight = e.shiftKey && (key === 'ArrowRight' || lower === 'd');

      if (wantsLeft || wantsRight) {
        e.preventDefault();
        const curIdx = focusOrder.indexOf(keyFocus);
        const nextIdx = wantsLeft ? Math.max(0, curIdx - 1) : Math.min(focusOrder.length - 1, curIdx + 1);
        const nextFocus = focusOrder[nextIdx];

        // Don't switch to chat panel unless chat tab is active (not log)
        if (nextFocus === 'chat') {
          const chatInput = document.querySelector('.chat-input');
          if (!chatInput) return; // Chat tab not active — do nothing
        }

        setKeyFocus(nextFocus);

        if (isStackedLayout) {
          setActivePanelIndex(nextIdx);
          if (nextIdx !== curIdx) flashPanel(nextIdx);
        }

        if (nextFocus === 'history') {
          const entries = gameState?.history ?? [];
          const entryCount = entries.length;
          // Park the keyboard on the synthetic live row when not already
          // viewing a specific entry, so arrow keys can immediately step
          // back through history.
          setHistoryFocusIndex(viewingIndex !== null ? viewingIndex : entryCount);
        }
        if (nextFocus === 'chat') {
          // Delay focus to allow panel transition to start
          setTimeout(() => {
            const input = document.querySelector('.chat-input');
            if (input) input.focus();
          }, 50);
        }
        return;
      }

      // ── History-focused keyboard navigation (linear view only) ──
      // Tree view handles its own keys internally via TreeHistory component
      if (keyFocus === 'history' && historyView === 'linear') {
        const entries = gameState?.history ?? [];
        const entryCount = entries.length;
        if (!entryCount) return;

        // entryCount is the index of the synthetic "live · Resume play"
        // pseudo-entry; the last real history entry sits at entryCount-1.
        const liveIndex = entryCount;
        const currentFocus = historyFocusIndex;

        if (key === 'ArrowUp' || lower === 'w') {
          e.preventDefault();
          const next = Math.max(0, Math.min(currentFocus, entryCount) - 1);
          setHistoryFocusIndex(next);
          handleHistoryClick(next);
          return;
        }
        if (key === 'ArrowDown' || lower === 's') {
          e.preventDefault();
          const next = Math.min(liveIndex, currentFocus + 1);
          setHistoryFocusIndex(next);
          handleHistoryClick(next);
          return;
        }
        if (key === 'Enter') {
          e.preventDefault();
          if (currentFocus < entryCount) {
            const toggleHandler = toggleJumpEntryRef.current;
            if (typeof toggleHandler === 'function') toggleHandler(currentFocus);
          }
          handleHistoryClick(currentFocus);
          return;
        }
        if (lower === 'l') {
          e.preventDefault();
          setViewingIndex(null);
          setHistoryFocusIndex(liveIndex);
          return;
        }
        return;
      }
      // Tree view is focused — let TreeHistory handle most keys, but handle L for go-live
      if (keyFocus === 'history') {
        if (lower === 'l') {
          e.preventDefault();
          setViewingIndex(null);
          const entries = gameState?.history ?? [];
          setHistoryFocusIndex(entries.length ? entries.length - 1 : 0);
        }
        return;
      }

      // ── Board-focused keyboard navigation ────────────────
      const boardVisible = !isStackedLayout || activePanelIndex === 1;
      if (!boardVisible) return;

      const perspective = yourPlayerNum || 1;
      const flipDir = perspective === 2 ? -1 : 1;

      switch (key) {
        case 'w': case 'ArrowUp':
          e.preventDefault();
          setCursorPos(prev => {
            if (!prev || !gameState?.board) return prev;
            const nr = Math.max(0, prev[0] - 1 * flipDir);
            return [nr, prev[1]];
          });
          break;
        case 's': case 'ArrowDown':
          e.preventDefault();
          setCursorPos(prev => {
            if (!prev || !gameState?.board) return prev;
            const nr = Math.min(gameState.board.length - 1, prev[0] + 1 * flipDir);
            return [nr, prev[1]];
          });
          break;
        case 'a': case 'ArrowLeft':
          e.preventDefault();
          setCursorPos(prev => {
            if (!prev || !gameState?.board) return prev;
            const nc = Math.max(0, prev[1] - 1 * flipDir);
            return [prev[0], nc];
          });
          break;
        case 'd': case 'ArrowRight':
          e.preventDefault();
          setCursorPos(prev => {
            if (!prev || !gameState?.board) return prev;
            const nc = Math.min((gameState.board[0]?.length || 1) - 1, prev[1] + 1 * flipDir);
            return [prev[0], nc];
          });
          break;
        case 'p':
          e.preventDefault();
          handlePlace();
          break;
        case 'j':
          e.preventDefault();
          handleJump();
          break;
        case 'Backspace':
          e.preventDefault();
          handleUndo();
          break;
        case 'Enter':
          e.preventDefault();
          handleConfirm();
          break;
        case 'f':
          e.preventDefault();
          boardControlsRef.current?.fitAndCenter();
          break;
        case 'c':
          e.preventDefault();
          boardControlsRef.current?.centerAtCurrentScale();
          break;
        case 'e':
          e.preventDefault();
          setCoordSystem(prev => prev === 'endzone' ? 'matrix' : 'endzone');
          break;
        case 'Escape':
          if (isPauseMenuOpen) break;
          e.preventDefault();
          if (stagedMove.type !== 'none') {
            setStagedMove({ type: 'none', path: [] });
          } else {
            setIsPauseMenuOpen(true);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isStackedLayout, activePanelIndex, yourPlayerNum, gameState, keyFocus, historyFocusIndex, viewingIndex, handlePlace, handleJump, handleUndo, handleConfirm, handleHistoryClick, flashPanel]);

  const handleSendChat = useCallback(() => {
    if (!chatInput.trim()) return;
    sendAction({ action: 'chat', message: chatInput.trim() });
    setChatInput('');
  }, [chatInput, sendAction]);

  const handleResign = useCallback(() => {
    sendAction({ action: 'resign' });
  }, [sendAction]);

  const handleStartGame = useCallback(() => {
    sendAction({ action: 'start_game' });
  }, [sendAction]);

  const handleRematch = useCallback(() => {
    setRematchRequested(true);
    sendAction({ action: 'rematch' });
  }, [sendAction]);

  // Bail out of the game without forfeiting. If the game is still active
  // we record the gameId in sessionStorage so the main menu can show a
  // "Rejoin game" countdown that lets the player come back within the
  // server's disconnect grace window. After that window, the opponent
  // wins by 'disconnect'.
  const handleExitGame = useCallback(() => {
    if (!gameOver) {
      sendAction({ action: 'leave' });
      try {
        sessionStorage.setItem('phutball_pending_online_game', JSON.stringify({
          gameId,
          opponentName: players?.player1?.userId === user?.id
            ? players?.player2?.displayName ?? 'Opponent'
            : players?.player1?.displayName ?? 'Opponent',
          leftAt: Date.now(),
          graceMs: 60_000,
        }));
      } catch { /* sessionStorage unavailable */ }
    } else {
      // Game already ended; nothing to rejoin.
      try { sessionStorage.removeItem('phutball_pending_online_game'); } catch {}
    }
    if (onExit) onExit('RETURN_TO_MENU');
  }, [gameOver, sendAction, onExit, gameId, players, user]);

  const handleHoverCell = (r, c) => setHoverPos([r, c]);
  const handleLeaveBoard = () => setHoverPos(null);
  const toggleCoordSystem = () => setCoordSystem(prev => prev === 'matrix' ? 'endzone' : 'matrix');

  // Online status for header (must be before early return)
  const onlineStatus = useMemo(() => {
    let text, color;
    if (status === 'Reconnecting...') {
      text = 'Reconnecting…'; color = '#ffb432';
    } else if (!opponentConnected) {
      text = 'Opp. disconnected'; color = '#ffb432';
    } else if (isYourTurn) {
      text = 'Your turn'; color = '#61f2ff';
    } else {
      text = "Opponent's turn"; color = undefined;
    }
    return { text, color, playerNum: yourPlayerNum };
  }, [status, opponentConnected, isYourTurn, yourPlayerNum]);

  const historyCurrentIndex = viewingIndex !== null ? viewingIndex : (gameState?.historyIndex ?? 0);
  const historyTreeWithViewing = useMemo(() => {
    const tree = gameState?.historyTree ?? null;
    if (!tree) return tree;
    // Hover preview deliberately does NOT change the tree's currentNodeId
    // — it's a separate visual layer (historyPreviewState drives the
    // board, stagedMove drives the trace). Touching currentNodeId on
    // every hover rebuilt the tree wrapper object, which cascaded
    // through pathNodes / scroll-metrics and tripped React error #185.
    // Local play does it this way too.
    if (viewingNodeId && nodesById.has(viewingNodeId)) {
      return { ...tree, currentNodeId: viewingNodeId };
    }
    if (viewingIndex === null) return tree;
    const find = (node) => {
      if (!node) return null;
      if (node.turn === viewingIndex) return node.id;
      for (const child of (node.children ?? [])) {
        const found = find(child);
        if (found) return found;
      }
      return null;
    };
    const fallbackId = find(tree.root);
    if (!fallbackId) return tree;
    return { ...tree, currentNodeId: fallbackId };
  }, [gameState?.historyTree, viewingIndex, viewingNodeId, nodesById]);

  if (!gameState) {
    return (
      <div className="app-container">
        <Header currentPlayer={1} playerNames={{}} onlineStatus={onlineStatus} />
        <div className="board-loading"><p>{status}</p></div>
      </div>
    );
  }

  const boardRows = gameState.board.length;
  const boardCols = gameState.board[0]?.length || 15;
  const effectivePerspective = yourPlayerNum || 1;

  const chatMessages = chatHistory.map(c => ({
    ...c,
    isOwn: c.userId === user?.id,
    playerNum: c.userId === players?.player1?.userId ? 1 : c.userId === players?.player2?.userId ? 2 : null,
  }));

  const makeIndicator = (position) => formatBoardCoordinate({
    position, numRows: boardRows, numCols: boardCols, coordSystem, viewPerspective: effectivePerspective,
  });
  const cursorIndicator = makeIndicator(cursorPos);
  const hoverIndicator = makeIndicator(hoverPos);

  // ── Panel content ─────────────────────────────────────────────
  const logPanel = (
    <LogPanel
      logHistory={gameState?.logHistory ?? []}
      chatEnabled={true}
      chatMessages={chatMessages}
      chatInput={chatInput}
      onChatInputChange={setChatInput}
      onChatSend={handleSendChat}
      currentMoveIndex={gameState.numTurns}
      playerConfig={{
        p1: players?.player1?.displayName || 'Player 1',
        p2: players?.player2?.displayName || 'Player 2',
      }}
    />
  );

  const isHostOnMove = clock?.active != null && yourPlayerNum === clock.active && !clockStarted && !gameOver;
  const showStartBanner = !clockStarted && !gameOver && timeControl !== 'live' && timeControl !== 'correspondence';

  const boardPanel = (
    <div className="board-container">
      {showStartBanner && (
        <div className="online-start-banner">
          {isHostOnMove ? (
            <>
              <span className="online-start-banner__text">Ready when you are</span>
              <button className="online-start-banner__btn" onClick={handleStartGame}>Start Game</button>
            </>
          ) : (
            <span className="online-start-banner__text">Waiting for opponent to start the game…</span>
          )}
        </div>
      )}
      <GameClocks
        clock={clock}
        clockReceivedAt={clockReceivedAt}
        timeControl={timeControl}
        players={players}
        yourPlayerNum={yourPlayerNum}
        gameOver={!!gameOver}
      />
      <div className="board-status-bar">
        <div className="board-indicator-group" aria-live="polite">
          <div className="coord-indicator">
            <div className="coord-indicator__label">Cursor</div>
            <div className="coord-indicator__value">
              <span className="coord-indicator__axis">R</span>
              <span>{cursorIndicator.rowLabel}</span>
              <span className="coord-indicator__divider">&middot;</span>
              <span className="coord-indicator__axis">C</span>
              <span>{cursorIndicator.colLabel}</span>
            </div>
          </div>
          <div className="coord-indicator coord-indicator--hover">
            <div className="coord-indicator__label">Hover</div>
            <div className="coord-indicator__value">
              <span className="coord-indicator__axis">R</span>
              <span>{hoverIndicator.rowLabel}</span>
              <span className="coord-indicator__divider">&middot;</span>
              <span className="coord-indicator__axis">C</span>
              <span>{hoverIndicator.colLabel}</span>
            </div>
          </div>
        </div>
        <button
          type="button"
          className="board-toggle"
          onClick={(e) => { toggleCoordSystem(); e.currentTarget.blur(); }}
        >
          <CoordinateSystemIcon mode={coordSystem} />
          <span>{coordSystem === 'matrix' ? 'matrix' : 'endzone relative'}</span>
        </button>
        <button
          type="button"
          className="board-toggle"
          onClick={(e) => { setShowMinimap(v => !v); e.currentTarget.blur(); }}
          aria-label={showMinimap ? 'Hide minimap' : 'Show minimap'}
          title={showMinimap ? 'Hide minimap' : 'Show minimap'}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="1" width="14" height="10" rx="1.5" />
            <circle cx="8" cy="6" r="2" />
            {!showMinimap && <line x1="2" y1="12" x2="14" y2="0" strokeWidth="2" stroke="currentColor" />}
          </svg>
          <span>{showMinimap ? 'minimap' : 'minimap off'}</span>
        </button>
      </div>

      <GameBoard
        board={isViewingHistory ? viewedBoard.board : gameState.board}
        phutballPos={isViewingHistory ? viewedBoard.phutballPos : gameState.phutballPos}
        onCellClick={handleCellClick}
        onHoverCell={handleHoverCell}
        onLeaveBoard={handleLeaveBoard}
        coordSystem={coordSystem}
        viewPerspective={effectivePerspective}
        liveNodeId={gameState?.historyTree?.liveNodeId ?? null}
        currentNodeId={
          // Match local play: don't fold the hover-preview node into the
          // ball's key — adjacent hovers should glide the ball one hop
          // (via atomic per-hop snapshots) instead of remounting.
          viewingNodeId
          ?? (isViewingHistory ? `online-history-${viewingIndex}` : (gameState?.historyTree?.liveNodeId ?? null))
        }
        isPreviewingStagedMove={treePreviewActive}
        cursorPos={isViewingHistory ? null : cursorPos}
        hoverPos={isViewingHistory ? null : hoverPos}
        stagedMove={treePreviewActive ? stagedMove : (isViewingHistory ? { type: 'none', path: [] } : stagedMove)}
        jumpPreview={isViewingHistory && !treePreviewActive ? emptyJumpPreview : jumpPreview}
        invalidPlacement={null}
        showMinimap={showMinimap}
        onControlsReady={(controls) => { boardControlsRef.current = controls; }}
      />

      <div className="action-bar">
        <button disabled={!canAct || !cursorPos} onClick={handlePlace}>Place</button>
        <button disabled={!canAct || !cursorPos} onClick={handleJump}>Jump</button>
        <button disabled={!canAct || stagedMove.type === 'none' || stagedMove.path.length === 0} onClick={handleConfirm}>Confirm</button>
        <button disabled={!canAct || stagedMove.type === 'none' || stagedMove.path.length === 0} onClick={handleUndo}>Undo</button>
      </div>
    </div>
  );

  const historyPanel = (
    <HistoryPanel
      history={gameState?.history ?? []}
      currentIndex={historyCurrentIndex}
      onHistoryClick={handleHistoryClick}
      onSelectTreeNode={handleSelectTreeNode}
      onPreviewTreeEdge={handlePreviewTreeEdge}
      previewedTreeEdge={previewedTreeEdge}
      treePreviewActive={treePreviewActive}
      registerToggleJumpEntry={registerToggleJumpEntry}
      onGoLive={isViewingHistory ? handleGoLive : null}
      viewMode={historyView}
      setViewMode={setHistoryView}
      historyTree={historyTreeWithViewing}
      keyFocus={keyFocus}
      onRequestFocus={setKeyFocus}
      focusedHistoryIndex={historyFocusIndex}
      coordSystem={coordSystem}
      boardRows={boardRows}
      boardCols={boardCols}
      viewPerspective={effectivePerspective}
    />
  );

  const panelContentMap = { log: logPanel, board: boardPanel, history: historyPanel };
  const appClassName = `app-container${isStackedLayout ? ' app-container--stacked' : ''}`;
  const mainClassName = `main-content${isStackedLayout ? ' stacked' : ''}`;
  const pagerClasses = ['panel-pager'];
  if (isPagerDragging) pagerClasses.push('panel-pager--dragging');
  const pagerStyle = isStackedLayout
    ? {
        width: `${PANEL_CONFIG.length * 100}%`,
        transform: `translateX(calc(-${activePanelIndex * 100 / PANEL_CONFIG.length}% + ${swipeOffsetPct / PANEL_CONFIG.length}%))`,
      }
    : undefined;

  return (
    <div
      className={appClassName}
      ref={isStackedLayout ? swipeContainerRef : undefined}
    >
      <button
        className="game-menu-toggle"
        onClick={(e) => { e.currentTarget.blur(); setIsPauseMenuOpen(true); }}
        aria-label="Open menu"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <rect x="2" y="4" width="16" height="2" rx="1" />
          <rect x="2" y="9" width="16" height="2" rx="1" />
          <rect x="2" y="14" width="16" height="2" rx="1" />
        </svg>
      </button>

      <PauseMenu
        isOpen={isPauseMenuOpen}
        onClose={() => setIsPauseMenuOpen(false)}
        onResume={() => setIsPauseMenuOpen(false)}
        onResign={() => { setIsPauseMenuOpen(false); handleResign(); }}
        onMainMenu={onExit ? handleExitGame : undefined}
        historyLength={gameState?.history?.length ?? 0}
        treeNodeCount={nodesById.size}
        stats={gameStats}
      />

      <Header
        currentPlayer={gameState.currentPlayer}
        playerNames={{
          p1: players?.player1?.displayName || 'Player 1',
          p2: players?.player2?.displayName || 'Player 2',
        }}
        isStackedLayout={isStackedLayout}
        activePanelIndex={activePanelIndex}
        onlineStatus={onlineStatus}
      />

      {isStackedLayout && (
        <div className="panel-indicator-bar">
          <div className="panel-indicator-track">
            {PANEL_CONFIG.map((panel, idx) => (
              <button
                key={`${panel.id}-dot`}
                className={`panel-dot${activePanelIndex === idx ? ' active' : ''}`}
                onClick={() => setActivePanelIndex(idx)}
                aria-label={panel.label}
              />
            ))}
          </div>
        </div>
      )}

      <main className={mainClassName}>
        {isStackedLayout ? (
          <div className={pagerClasses.join(' ')} style={pagerStyle}>
            {PANEL_CONFIG.map((panel, idx) => {
              const innerClasses = ['panel-page-inner'];
              if (panel.id === 'board') innerClasses.push('panel-page-inner--board');
              if (panel.id === 'history') innerClasses.push('panel-page-inner--history');
              const pageClasses = ['panel-page'];
              if (panelFlash === idx) pageClasses.push('panel-page--flash');
              return (
                <section
                  key={panel.id}
                  className={pageClasses.join(' ')}
                  role="tabpanel"
                  aria-label={panel.label}
                >
                  <div className={innerClasses.join(' ')}>
                    {panelContentMap[panel.id]}
                  </div>
                </section>
              );
            })}
          </div>
        ) : (
          <>
            {logPanel}
            {boardPanel}
            {historyPanel}
          </>
        )}
      </main>

      {gameOver && (
        <div className="online-game-over-overlay">
          <div className="online-game-over-modal">
            <h2 className="online-game-over-title">
              {gameOver.winner === (yourPlayerNum === 1 ? 'player1' : 'player2')
                ? 'You Win!'
                : gameOver.winner === 'draw'
                ? 'Draw!'
                : 'You Lose'}
            </h2>
            {gameOver.reason && (
              <p className="online-game-over-reason">
                {gameOver.reason === 'resignation' ? 'by resignation' : ''}
              </p>
            )}
            {gameOver.elo && (() => {
              const delta = yourPlayerNum === 1 ? gameOver.elo.player1Delta : gameOver.elo.player2Delta;
              const newElo = yourPlayerNum === 1 ? gameOver.elo.player1New : gameOver.elo.player2New;
              return (
                <p className="online-game-over-elo">
                  <span className={delta >= 0 ? 'elo-positive' : 'elo-negative'}>
                    {delta >= 0 ? '+' : ''}{delta} ELO
                  </span>
                  {' '}({newElo})
                </p>
              );
            })()}
            <div className="online-game-over-actions">
              {rematchOffered && !rematchRequested ? (
                <button className="online-game-btn online-game-btn--primary" onClick={handleRematch}>
                  Accept Rematch
                </button>
              ) : rematchRequested ? (
                <button className="online-game-btn" disabled>
                  Rematch requested...
                </button>
              ) : (
                <button className="online-game-btn online-game-btn--primary" onClick={handleRematch}>
                  Rematch
                </button>
              )}
              <button className="online-game-btn" onClick={handleExitGame}>
                Main Menu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnlineGame;
